import { useEffect, useRef } from 'react';
import { useGlobalStore } from '@digiwedge/global-state';
import { getTokenDeliveryMode } from '@digiwedge/idp-client';
import { useRefresh } from './hooks-auth.js';
import { clearCookieAuthPending, hasCookieAuthPending } from './cookie-auth.js';
import { isUnauthenticatedRefreshError } from './refresh-error.js';
// Constants for storage keys
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const JTI_KEY = 'jti';
export function useRehydrateTokens() {
    const storeLogin = useGlobalStore((state) => state.login);
    const { refresh } = useRefresh();
    const hasBootstrapped = useRef(false);
    const refreshRef = useRef(refresh);
    const storeLoginRef = useRef(storeLogin);
    useEffect(() => {
        refreshRef.current = refresh;
    }, [refresh]);
    useEffect(() => {
        storeLoginRef.current = storeLogin;
    }, [storeLogin]);
    useEffect(() => {
        if (hasBootstrapped.current)
            return;
        hasBootstrapped.current = true;
        let cancelled = false;
        const boot = async () => {
            try {
                const cookiePending = hasCookieAuthPending();
                if (getTokenDeliveryMode() === 'cookie' || cookiePending) {
                    if (getTokenDeliveryMode() === 'cookie' && !cookiePending) {
                        return;
                    }
                    try {
                        await refreshRef.current({});
                    }
                    catch (error) {
                        if (isUnauthenticatedRefreshError(error)) {
                            return;
                        }
                        throw error;
                    }
                    finally {
                        if (cookiePending)
                            clearCookieAuthPending();
                    }
                    return;
                }
                // Prefer localStorage ("Remember me") but fall back to sessionStorage
                const ls = localStorage;
                const ss = sessionStorage;
                const token = ls.getItem(ACCESS_TOKEN_KEY) || ss.getItem(ACCESS_TOKEN_KEY);
                const refreshToken = ls.getItem(REFRESH_TOKEN_KEY) || ss.getItem(REFRESH_TOKEN_KEY);
                const jti = ls.getItem(JTI_KEY) || ss.getItem(JTI_KEY);
                if (!cancelled && token) {
                    // If it came from sessionStorage, do not force-remember; otherwise keep it in localStorage
                    const remember = !!ls.getItem(ACCESS_TOKEN_KEY);
                    storeLoginRef.current(token, refreshToken || undefined, jti || undefined, remember);
                }
            }
            catch (error) {
                if (!cancelled) {
                    if (getTokenDeliveryMode() === 'cookie' && isUnauthenticatedRefreshError(error)) {
                        return;
                    }
                    console.error('Unable to rehydrate auth tokens from storage:', error);
                }
            }
        };
        void boot();
        return () => {
            cancelled = true;
        };
    }, []);
}
//# sourceMappingURL=useRehydrateTokens.js.map